import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { photoEngagementService } from '../../services/photoEngagementService';

export default function PhotoEngagementPanel({ harvestId }) {
  const [comments, setComments] = useState({ results: [], count: 0, next: null, prev: null });
  const [commentPage, setCommentPage] = useState(1);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [reactionsSummary, setReactionsSummary] = useState([]);
  const [reacting, setReacting] = useState(false);
  const inputRef = useRef(null);

  const loadComments = useCallback(async (page = 1) => {
    if (!harvestId) return;
    try {
      setLoadingComments(true);
      const data = await photoEngagementService.listComments(harvestId, page);
      setComments(data || { results: [], count: 0 });
      setCommentPage(page);
    } catch (err) {
      // Swallow errors (403/500) and disable interactions gracefully
      setComments({ results: [], count: 0, next: null, prev: null });
      setCanInteract(false);
    } finally {
      setLoadingComments(false);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    }
  }, [harvestId]);

  const loadReactions = useCallback(async () => {
    if (!harvestId) return;
    try {
      const data = await photoEngagementService.getReactionsSummary(harvestId);
      setReactionsSummary(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, [harvestId]);

  useEffect(() => {
    if (harvestId) {
      loadComments(1);
      loadReactions();
    }
  }, [harvestId, loadComments, loadReactions]);

  const [canInteract, setCanInteract] = useState(true);

  // Probe interact permission by attempting a toggle call with no-op emoji via OPTIONS
  // Instead, we infer from GET comments: if GET works but POST 403s later, we disable UI gracefully
  useEffect(() => {
    setCanInteract(true);
  }, [harvestId]);

  return (
    <Card className="p-2" onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.stopPropagation(); } }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong className="me-2">Reactions</strong>
        <div className="d-flex gap-1">
          {['👍','❤️','🦌','🌽','🏹'].map(e => (
            <button
              key={e}
              className="btn btn-sm btn-outline-secondary"
              disabled={reacting || !harvestId || !canInteract}
              onClick={async () => { 
                try { 
                  setReacting(true); 
                  await photoEngagementService.toggleReaction(harvestId, e); 
                  await loadReactions(); 
                } catch (err) {
                  // If 403, disable interactions and keep UI friendly
                  if (err?.response?.status === 403) setCanInteract(false);
                } finally { setReacting(false); } 
              }}
              title="React"
              style={!canInteract ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
            >{e}</button>
          ))}
        </div>
      </div>
      <div className="mb-2" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {reactionsSummary.length === 0 ? (
          <span className="text-muted" style={{ fontSize: 12 }}>No reactions yet</span>
        ) : reactionsSummary.map(g => (
          <span key={g.emoji} className="badge bg-light text-dark" title={(g.users||[]).map(u=>u.username).join(', ')}>
            <span style={{ marginRight: 4 }}>{g.emoji}</span>{g.count}
          </span>
        ))}
      </div>
      <strong className="mb-2">Comments</strong>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {loadingComments ? (
          <div className="text-muted">Loading…</div>
        ) : comments.results.length === 0 ? (
          <div className="text-muted">No comments yet</div>
        ) : (
          comments.results.map(c => (
            <div key={c.id} className="mb-2">
              <div style={{ fontSize: 13 }}>
                <strong>@{c.user?.username}</strong>
                <span className="text-muted" style={{ marginLeft: 6, fontSize: 12 }}>{new Date(c.created_at).toLocaleString()}</span>
                {c.edited_at && <span className="text-muted" style={{ marginLeft: 6, fontSize: 12 }}>(edited)</span>}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
            </div>
          ))
        )}
        {comments.count > comments.results.length && (
          <div className="d-grid">
            <button className="btn btn-sm btn-outline-secondary" onClick={() => loadComments(commentPage + 1)}>Load more</button>
          </div>
        )}
      </div>
      <div className="mt-2 d-flex gap-2" style={!canInteract ? { opacity: 0.6 } : undefined}>
        <input
          ref={inputRef}
          className="form-control form-control-sm"
          placeholder="Write a comment…"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          maxLength={255}
          onClick={(e) => e.stopPropagation()}
          disabled={!canInteract}
        />
        <button
          className="btn btn-sm btn-primary"
          disabled={!harvestId || newComment.trim().length < 2 || !canInteract}
          onClick={async () => {
            try {
              await photoEngagementService.addComment(harvestId, { text: newComment.trim() });
              setNewComment('');
              await loadComments(1);
              setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
            } catch (err) {
              if (err?.response?.status === 403) setCanInteract(false);
            }
          }}
        >Send</button>
      </div>
      {!canInteract && (
        <div className="text-muted small mt-2">Only the owner and hunting buddies can comment or react.</div>
      )}
    </Card>
  );
}


